import React from 'react';
import PropTypes from 'prop-types';
import './Details.css';
import Box from '@mui/joy/Box';
import AccordionGroup from '@mui/joy/AccordionGroup';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';
import Typography from '@mui/joy/Typography';
import Avatar from '@mui/joy/Avatar';
import DescriptionIcon from '@mui/icons-material/Description';
import EventIcon from '@mui/icons-material/Event';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import Link from '@mui/joy/Link';
import Card from '@mui/joy/Card';
import Stepper from '@mui/joy/Stepper';
import StepIndicator from '@mui/joy/StepIndicator';
import Step from '@mui/joy/Step';
import avatar from '../../Male_Avatar.jpg'
import { Stack } from '@mui/joy';


function Details({bill}) {
  const sortedActions = bill?.actions ? bill?.actions : [];

  // Find the last action
  const lastAction = sortedActions[0];

  // Predefined history steps
  const historySteps = [
    { title: 'Introduced', type:'IntroReferral', source: 'Library of Congress', description: 'The bill is formally presented in the House or Senate.', icon: <DescriptionIcon/> },
    { title: 'On Schedule', type: 'Calendars', description: 'The bill is scheduled for discussion on the House or Senate floor.', icon: <EventIcon/> },
    { title: 'Passed Committee', type: 'Committee', source: 'Library of Congress', description: 'The bill is reviewed and voted on by a designated committee.', icon: <HowToRegIcon/> },
    { title: 'Passed House', type: 'Floor', source: 'House', description: 'The bill is debated and voted on by the entire House.', icon: <HowToRegIcon/> },
    { title: 'Passed Senate', type: 'Floor', source: 'Senate', description: 'The bill is debated and voted on by the Senate.', icon: <HowToRegIcon/> },
    { title: 'Signed by President', type: 'BecameLaw', source: 'President', description: 'The bill is signed into law by the President.', icon: <BorderColorIcon/> },
  ];
  
  // Determine completed steps
  const completedSteps = historySteps.map((step) => ({
    ...step,
    completed: sortedActions.length > 0 ? sortedActions.some((action) => action.type === step.type && (step.type === 'Calendars' || action.sourceSystem.name === step.source)) : [],
  }));

  return (
    <Stack direction={"column"} spacing={4} padding={4} sx={{ paddingBottom: '40px'}}>
      <Typography level="h2">
        Details
      </Typography>
      <Card
        variant="outlined"
        color="neutral"
        size="md"
        orientation="horizontal"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '55%' }}>
          <Typography level="title-lg"> Current status </Typography>
          <div className="author">
            {historySteps.find(item => item.type === lastAction?.type)?.icon}
            <Typography>{lastAction?.text || "No actions available."}</Typography>
          </div>
        </Box>
      </Card>
      
      <AccordionGroup 
        variant="outlined"
        color="neutral"
        transition="0.2s ease"
        size='lg'
        sx={{backgroundColor: 'white', borderRadius: 'md'}}
      >
        <Accordion
          color="neutral"
        >
          <AccordionSummary>
            <Avatar color="primary">
              <HistoryIcon />
            </Avatar>
            <Typography level="title-lg" sx={{textAlign: 'left', width: '100%'}}>History</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className="history-container" sx={{padding: 1}}>
              <Stepper 
                orientation="vertical"
                size="lg"
                sx={{
                  width: '100%',
                  '--StepIndicator-size': '3rem',
                }}
              >
                {completedSteps.map((step, index) => (
                  <Step 
                    key={index} 
                    indicator={
                      <StepIndicator 
                        variant={step.completed ? "solid" : "outlined"} 
                        color="primary"
                      >
                        {step.icon}
                      </StepIndicator>
                    }>
                      <Typography level="body-md">{step.title}</Typography>
                      <Typography level="body-sm">
                        {step.description}
                      </Typography>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>
            <Avatar color="primary">
              <PeopleIcon />
            </Avatar>
            <Typography level="title-lg" sx={{textAlign: 'left', width: '100%'}}>Sponsors & cosponsors</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{padding: 1}}>
              <Box sx={{padding: 1}}>
                <Typography level="title-md">Sponsor</Typography>
                <div className="author">
                    <img src={bill?.sponsor?.imageUrl || bill?.authorImage || avatar} alt={bill?.sponsor?.name} />
                    <div className='text'>
                        <Link to="/"><Typography level="title-md">{bill?.sponsor?.name}</Typography></Link>
                        <Typography level="body-sm">{bill?.sponsor?.party} - {bill?.sponsor?.state}</Typography>
                    </div>
                </div>
              </Box>
                
              <Box sx={{padding: 1}}>
                <Typography level="title-md">Cosponsors</Typography>
                <Box className="coauthor-container">
                  {bill?.cosponsors?.length > 0 ? 
                    bill.cosponsors.map((cosponsor, index) => (
                      <div className="author"  key={index}>
                        <img src={cosponsor.authorImage || avatar} alt={cosponsor.name} />
                        <div className='text'>
                            <Link to="/"><Typography level="title-md">{cosponsor.name}</Typography></Link>
                            <Typography level="body-sm">{cosponsor.party} - {cosponsor.state}</Typography>
                        </div>
                      </div>
                    )) : 
                    <Typography level="body-md">There are no cosponsors at this time.</Typography>
                  }
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </AccordionGroup>
    </Stack>
  );
};

  
// Prop types for our Component
Details.propTypes = {
    bill: PropTypes.object.isRequired,
}
  
export default Details;